import { useRestApiProvider } from "@jugl-web/rest-api";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useMemo } from "react";

/** Fallback ticket allowance used until the subscription entitlement is loaded. */
const DEFAULT_TICKETS_LIMIT = 20;

/**
 * The tickets allowance currently reuses the `jugl::tasks` entitlement — there is
 * no dedicated `jugl::tickets` key yet. It is referenced in one place so switching
 * to a future key is a one-line change.
 */
const TICKETS_ENTITLEMENT_KEY = "jugl::tasks" as const;

export interface TicketsLimit {
  /** Total number of tickets in the workspace (including resolved). */
  usedCount: number;
  /** Maximum tickets allowed by the current plan. */
  limit: number;
  /** True when the plan allows an unlimited number of tickets. */
  isUnlimited: boolean;
  /** True when the total ticket count has reached the plan limit. */
  isLimitReached: boolean;
  /** True when the workspace subscription is canceled. */
  isSubscriptionCanceled: boolean;
  /** True when creating a new ticket must be blocked (limit reached or canceled). */
  isTicketCreationBlocked: boolean;
}

export const useTicketsLimit = (): TicketsLimit => {
  const { entityId } = useEntitySelectedProvider();
  const { ticketsApi } = useRestApiProvider();
  const { liteSubscription, isSubscriptionCanceled } = useSubscriptionV2();

  const { data: ticketsStats } = ticketsApi.useGetTicketsStatsQuery(
    { entityId },
    { refetchOnMountOrArgChange: true }
  );

  return useMemo(() => {
    const rawLimit = liteSubscription?.entitlements?.[TICKETS_ENTITLEMENT_KEY];
    const isUnlimited = rawLimit === null;
    const limit = rawLimit ?? DEFAULT_TICKETS_LIMIT;

    // The backend limits by the total number of tickets, so count every status
    // including resolved (the `all` stat excludes resolved tickets).
    const usedCount =
      (ticketsStats?.in_progress ?? 0) +
      (ticketsStats?.on_customer ?? 0) +
      (ticketsStats?.on_hold ?? 0) +
      (ticketsStats?.issued ?? 0) +
      (ticketsStats?.resolved ?? 0);

    const isLimitReached = !isUnlimited && usedCount >= limit;
    const isTicketCreationBlocked = isSubscriptionCanceled || isLimitReached;

    return {
      usedCount,
      limit,
      isUnlimited,
      isLimitReached,
      isSubscriptionCanceled,
      isTicketCreationBlocked,
    };
  }, [liteSubscription, ticketsStats, isSubscriptionCanceled]);
};
